import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { SidebarService } from '../../services/components/sidebar/sidebar.service';
import { Router, NavigationEnd } from '@angular/router';
import { environment } from 'src/environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from '../../services/auth/auth.service';
import { UserModeService } from 'src/app/services/customs/user-mode/user-mode.service'
import { filter, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() isCollapsed = false;
  @Output() collapsedChange = new EventEmitter<boolean>();

  submenuVisibility: boolean[] = [];
  currentUrl: string = '';
  menuOptions: any[] = [];
  public showSwitchOption = false;
  mainAppBaseURL: string = '';
  activeMode: 'customer' | 'provider' = 'customer';
  cookieName: string = '';

  isHovered = false;
  private savedScrollTop = 0;

  // Flyout and Label positioning
  flyoutVisibility: boolean[] = [];
  flyoutTops: { [key: number]: number } = {};
  labelTops: { [key: number]: number } = {};
  private flyoutCloseTimers: { [key: number]: any } = {};

  constructor(
    private sidebarService: SidebarService,
    private router: Router,
    private cookieService: CookieService,
    private authService: AuthService,
    private userModeService: UserModeService
  ) {
    this.mainAppBaseURL = environment.appUrl;
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = event.urlAfterRedirects;
      }
    });
  }

  ngOnInit(): void {
    this.userModeService.hasProvider$.subscribe((hasProvider) => {
      this.showSwitchOption = hasProvider;
    });

    this.userModeService.mode$
      .pipe(
        filter((mode): mode is 'customer' | 'provider' => mode !== null),
        switchMap((mode) => {
          this.activeMode = mode;
          return this.sidebarService.getSidebar(mode);
        })
      )
      .subscribe((menu) => {
        this.menuOptions = menu;
        this.sidebarService.setActiveTab();
      });
  }

  switchMode(): void {
    const newMode: 'customer' | 'provider' =
      this.activeMode === 'customer' ? 'provider' : 'customer';

    this.userModeService.setMode(newMode);

    this.router.navigate(['/dashboard']);
  }

  toggleSubmenu(index: number): void {
    this.submenuVisibility[index] = !this.submenuVisibility[index];
  }

  private isMobile(): boolean {
    return window.innerWidth < 768;
  }

  onMouseEnter() {
    if (this.isMobile()) return;
    this.isHovered = true;
    this.isCollapsed = false;
    this.collapsedChange.emit(this.isCollapsed);

    // Restore scroll position after expand
    requestAnimationFrame(() => {
      const el = this.getScrollableElement();
      if (el) el.scrollTop = this.savedScrollTop;
    });
  }

  onMouseLeave() {
    if (this.isMobile()) return;

    // Save scroll position before collapse
    const el = this.getScrollableElement();
    if (el) this.savedScrollTop = el.scrollTop;

    this.isHovered = false;
    this.isCollapsed = true;
    this.collapsedChange.emit(this.isCollapsed);
    this.closeAllFlyouts();
  }

  private getScrollableElement(): HTMLElement | null {
    const sidebar = document.querySelector('.sidebar-dynamic');
    return sidebar as HTMLElement | null;
  }

  isChildActive(menuItem: any): boolean {
    if (!menuItem.children || menuItem.children.length === 0) {
      return false;
    }
    return menuItem.children.some((child: { route: string }) =>
      this.isRouteActive(child.route)
    );
  }

  isSubmenuVisible(index: number): boolean {
    return this.submenuVisibility[index];
  }

  get _logoutURL() {
    return `${this.mainAppBaseURL}/logout`;
  }

  get _helpURL() {
    return 'https://help.truckit.net/support/home';
  }

  get _howItWorksURL() {
    return `${this.mainAppBaseURL}/how-it-works`;
  }

  getCurrentActiveTab() {
    return this.sidebarService.getActiveTab();
  }

  isRouteActive(route: string): boolean {
    const normalize = (url: string): string => url.replace(/^\/+/, '').replace(/\/+$/, '');
    const normalizedRoute = normalize(route);
    const normalizedCurrentUrl = normalize(this.currentUrl);

    if (normalizedRoute == '') return false;
    return (
      normalizedCurrentUrl === normalizedRoute ||
      normalizedCurrentUrl.startsWith(normalizedRoute)
    );
  }

  public navigate(route: string) {
    this.closeAllFlyouts();

    if (route.startsWith('http')) {
      window.location.href = route;
    } else {
      this.router.navigate([route]).finally(() => {
        this.sidebarService.setActiveTab();
      });
    }
  }

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
    if (this.isCollapsed) {
      this.isHovered = false;
    }
    this.collapsedChange.emit(this.isCollapsed);
    this.closeAllFlyouts();
  }

  // --- Flyout hover logic ---

  onMenuItemEnter(index: number, event: MouseEvent) {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.flyoutTops[index] = rect.top;
    this.labelTops[index] = rect.top + (rect.height / 2);

    if (this.flyoutCloseTimers[index]) {
      clearTimeout(this.flyoutCloseTimers[index]);
      delete this.flyoutCloseTimers[index];
    }
    this.flyoutVisibility.forEach((_, i) => {
      if (i !== index) {
        this.flyoutVisibility[i] = false;
      }
    });
    this.flyoutVisibility[index] = true;
  }

  onMenuItemLeave(index: number) {
    this.flyoutCloseTimers[index] = setTimeout(() => {
      this.flyoutVisibility[index] = false;
      delete this.flyoutCloseTimers[index];
    }, 300);
  }

  onFlyoutEnter(index: number) {
    if (this.flyoutCloseTimers[index]) {
      clearTimeout(this.flyoutCloseTimers[index]);
      delete this.flyoutCloseTimers[index];
    }
  }

  onFlyoutLeave(index: number) {
    this.flyoutVisibility[index] = false;
  }

  private closeAllFlyouts() {
    this.flyoutVisibility = [];
    Object.values(this.flyoutCloseTimers).forEach(timer => clearTimeout(timer));
    this.flyoutCloseTimers = {};
  }

  ngOnDestroy() {
    Object.values(this.flyoutCloseTimers).forEach(timer => clearTimeout(timer));
  }
}
